import clsx from 'clsx'
import React, { Dispatch, FC, ReactNode, SetStateAction, useEffect, useRef } from 'react'

import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import { LoadingSpinner } from '@Components/Loading'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useScrollTo } from '@Globals/useScrollTo'

import styles from './CollapsableSection.module.scss'

export interface CollapsableSectionProps {
  autoScroll?: unknown
  canCollapse?: boolean
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  hideSpinner?: boolean
  icon?: ReactNode
  id?: string
  isCollapsed?: boolean
  isLoading?: boolean
  onCollapsedChanged?: Dispatch<SetStateAction<boolean>>
  title?: string | undefined
}

export const CollapsableSection: FC<CollapsableSectionProps> = ({
  autoScroll = false,
  canCollapse = true,
  children,
  className,
  disabled,
  hideSpinner,
  icon,
  id,
  isCollapsed = false,
  isLoading,
  onCollapsedChanged,
  title,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const ref = useRef(null)
  const scrollToRef = useScrollTo(ref, 0)

  useEffect(() => {
    if (!isCollapsed && autoScroll && ref.current) {
      scrollToRef()
    }
  }, [isCollapsed, ref, autoScroll, scrollToRef])
  const handleToggleCollapse = () => {
    if (onCollapsedChanged) {
      onCollapsedChanged(!isCollapsed)
    }
  }
  return (
    <div
      className={clsx(
        className,
        styles.collapsableSectionWrapper,
        isCollapsed ? styles.collapsed : styles.expanded,
        disabled && styles.disabled,
        cofDesignV1 && styles.cofDesignV1,
      )}
      id={id}
      ref={ref}
    >
      {isLoading && <div className={styles.loading}><LoadingSpinner className={clsx(hideSpinner && styles.hidden)} /></div>}
      {title && (
        <button
          className={clsx(
            styles.header,
            canCollapse && styles.canCollapse,
            !isCollapsed && styles.expanded,
          )}
          onClick={() => canCollapse && handleToggleCollapse()}
          type="button"
        >
          <span className={styles.titleWrapper}>
            {icon && <span className={styles.titleIcon}>{icon}</span>}
            <span className={styles.title}>{title}</span>
          </span>
          {canCollapse && (
            <SvgAngleLeft className={clsx(
              !isCollapsed && styles.isOpen,
              isCollapsed && styles.isClosed,
            )}
            />
          )}
        </button>
      )}
      <div className={clsx(styles.content, isCollapsed && styles.hidden, 'collapse-content')}>
        {children}
      </div>
    </div>
  )
}
